import type {
  CreateTaskInput,
  MeDto,
  NoteDto,
  Priority,
  TaskDetailDto,
  TaskPatchBody,
  UserBrief,
} from '@ovo/shared'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { errorMessage } from '../api/client.ts'
import {
  qk,
  useCreateProject,
  useCreateTask,
  useDurationHint,
  useMe,
  usePatchTask,
  useProjects,
  useSimilarTasks,
  useSuggestDod,
  useTask,
  useTaskAction,
  useUpdateNote,
  useUserLoad,
  useUsers,
} from '../api/hooks.ts'
import { useDebounced } from '../lib/hooks.ts'
import { shortName } from '../lib/status.ts'
import { readStore, removeStore, writeStore } from '../lib/storage.ts'
import { useMainButton } from '../telegram/buttons.tsx'
import { useClosingConfirmation, useGoBack } from '../telegram/navigation.ts'
import { Button } from '../ui/Button.tsx'
import { Chip } from '../ui/Chip.tsx'
import type { DeadlineValue } from '../ui/DeadlineChips.tsx'
import { DeadlineChips } from '../ui/DeadlineChips.tsx'
import { EmptyState, ErrorState } from '../ui/EmptyState.tsx'
import { Field, Input, SwitchRow, Textarea } from '../ui/Input.tsx'
import { IconChevronDown, IconSearch } from '../ui/icons.tsx'
import { Section } from '../ui/List.tsx'
import { PersonPicker } from '../ui/PersonPicker.tsx'
import {
  EMPTY_PUBLISH,
  PublishCheckFields,
  type PublishValue,
  publishBody,
  publishInvalid,
} from '../ui/PublishCheck.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SegmentedControl } from '../ui/SegmentedControl.tsx'
import { SkeletonBlock } from '../ui/Skeleton.tsx'
import { snackbar } from '../ui/Snackbar.tsx'

interface FormState {
  title: string
  assignee: UserBrief | null
  /** Ещё исполнители: задача общая, закрыть может любой. */
  coAssignees: UserBrief[]
  /** Подпункты — по одному на строку (только при создании). */
  checklistText: string
  /** Проект или клиент (метка). */
  projectId: number | null
  due: DeadlineValue
  priority: Priority
  dod: string
  description: string
  requiresAcceptance: boolean
  /** Кто принимает работу; null — постановщик. */
  reviewer: UserBrief | null
  requiresResult: boolean
  watchers: UserBrief[]
  /** Проверка публикации в канале (только при создании). */
  publish: PublishValue
}

const EMPTY: FormState = {
  title: '',
  assignee: null,
  coAssignees: [],
  checklistText: '',
  projectId: null,
  due: { dueAt: null, dueKind: 'datetime' },
  priority: 'normal',
  dod: '',
  description: '',
  requiresAcceptance: false,
  reviewer: null,
  requiresResult: false,
  watchers: [],
  publish: EMPTY_PUBLISH,
}

const DRAFT_KEY = 'taskDraft'
const RECENT_KEY = 'recentAssignees'

const PRIORITY_OPTIONS: Array<{ value: Priority; label: string; attn?: boolean }> = [
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочно', attn: true },
]

function meBrief(me: MeDto): UserBrief {
  return {
    id: me.id,
    displayName: me.displayName,
    position: me.position,
    departmentName: me.department?.name ?? null,
  }
}

function fromTask(t: TaskDetailDto): FormState {
  return {
    // Проверка публикации задаётся только при создании — в форме правки её нет
    publish: EMPTY_PUBLISH,
    title: t.title,
    assignee: { id: t.assignee.id, displayName: t.assignee.displayName, position: '', departmentName: null },
    coAssignees: t.coAssignees.map((u) => ({ ...u, position: '', departmentName: null })),
    checklistText: '',
    projectId: t.project?.id ?? null,
    due: { dueAt: t.dueAt, dueKind: t.dueKind },
    priority: t.priority,
    dod: t.dod ?? '',
    description: t.description ?? '',
    requiresAcceptance: t.requiresAcceptance,
    reviewer: t.reviewer ?? null,
    requiresResult: t.requiresResult,
    watchers: t.watchers,
  }
}

/** Как на сервере (packages/app/src/load.ts): много открытых или срочных — предупреждаем. */
function overloaded(
  load: { open: number; urgent: number; limits: { open: number; urgent: number } },
  priority: Priority,
): boolean {
  return (
    load.open >= load.limits.open ||
    load.urgent >= load.limits.urgent ||
    (priority === 'urgent' && load.urgent >= load.limits.urgent - 1)
  )
}

/** Подпункты из текста: по одному на строку, без маркеров списка. */
function checklistItems(text: string): { text: string }[] {
  return text
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[-–•*]|\d+[.)])\s*/, '').trim())
    .filter((l) => l.length > 0)
    .slice(0, 30)
    .map((l) => ({ text: l.slice(0, 200) }))
}

function pushRecent(u: UserBrief, meId: number): void {
  if (u.id === meId) return
  const list = readStore<UserBrief[]>(RECENT_KEY, []).filter((x) => x.id !== u.id)
  writeStore(RECENT_KEY, [u, ...list].slice(0, 5))
}

function Collapsible({
  title,
  count,
  children,
  defaultOpen,
}: {
  title: string
  count?: number
  children: ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen))
  return (
    <div className="stack stack--tight">
      <button
        type="button"
        className="collapsible__toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {title}
        {count ? ` · ${count}` : ''}
        <IconChevronDown size={18} className={open ? 'rotate-180' : undefined} />
      </button>
      {open && children}
    </div>
  )
}

export function TaskFormScreen() {
  const { id } = useParams()
  const editId = id ? Number(id) : null
  const isEdit = editId !== null
  const [params] = useSearchParams()
  const prefillId = Number(params.get('assignee')) || null
  const prefillProject = Number(params.get('project')) || null
  // «→ Задачей» из заметки на главной: первая строка — название, остальное — описание
  const prefillNoteId = Number(params.get('note')) || null
  const qc = useQueryClient()
  const prefillNote = prefillNoteId
    ? (qc.getQueryData<NoteDto[]>(qk.notes)?.find((n) => n.id === prefillNoteId) ?? null)
    : null
  const linkNote = useUpdateNote()
  const navigate = useNavigate()
  const goBack = useGoBack()
  const me = useMe()
  const meId = me.data?.id ?? 0
  const existing = useTask(editId)
  const create = useCreateTask()
  const patch = usePatchTask()
  const action = useTaskAction()

  const [form, setForm] = useState<FormState>(() => {
    if (isEdit) return EMPTY
    if (prefillNote) {
      const [first = '', ...rest] = prefillNote.text.split('\n')
      const title = first.trim().slice(0, 200)
      const tail = [first.trim().slice(200), ...rest].join('\n').trim()
      return { ...EMPTY, title, description: tail }
    }
    const draft = readStore<FormState | null>(DRAFT_KEY, null)
    const base = draft && !prefillId ? { ...EMPTY, ...draft } : EMPTY
    return prefillProject ? { ...base, projectId: prefillProject } : base
  })
  const [initial, setInitial] = useState<FormState | null>(isEdit ? null : EMPTY)
  const [picker, setPicker] = useState<'assignee' | 'watchers' | 'co' | 'reviewer' | null>(null)
  const [touched, setTouched] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)
  const restoredRef = useRef(false)

  // правка: заполняем форму, когда задача загрузилась
  useEffect(() => {
    if (isEdit && existing.data && initial === null) {
      const s = fromTask(existing.data)
      setForm(s)
      setInitial(s)
    }
  }, [isEdit, existing.data, initial])

  // предзаполнение исполнителя из ?assignee=
  const users = useUsers('', Boolean(prefillId) && !isEdit)
  useEffect(() => {
    if (!prefillId || isEdit) return
    const u = users.data?.find((x) => x.id === prefillId)
    if (u) setForm((f) => (f.assignee ? f : { ...f, assignee: u }))
  }, [prefillId, isEdit, users.data])

  // восстановленный черновик
  useEffect(() => {
    if (isEdit || restoredRef.current) return
    restoredRef.current = true
    const draft = readStore<FormState | null>(DRAFT_KEY, null)
    if (draft && !prefillId && !prefillNote && draft.title) {
      snackbar.show({
        text: 'Восстановили несохранённую задачу',
        action: {
          label: 'Очистить',
          onClick: () => {
            removeStore(DRAFT_KEY)
            setForm(EMPTY)
          },
        },
      })
    }
  }, [isEdit, prefillId, prefillNote])

  // автофокус на название при создании
  useEffect(() => {
    if (!isEdit) titleRef.current?.focus()
  }, [isEdit])

  const dirty = isEdit
    ? initial !== null && JSON.stringify(form) !== JSON.stringify(initial)
    : Boolean(form.title.trim() || form.description.trim() || form.dod.trim())
  useClosingConfirmation(dirty)

  // черновик создания — в localStorage
  useEffect(() => {
    if (isEdit) return
    if (dirty) writeStore(DRAFT_KEY, form)
  }, [form, dirty, isEdit])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const self = form.assignee !== null && form.assignee.id === meId
  const load = useUserLoad(form.assignee && !self ? form.assignee.id : null)
  const suggest = useSuggestDod()
  const projects = useProjects()
  const createProject = useCreateProject()
  const [newProject, setNewProject] = useState('')
  // Похожая открытая задача у тех же исполнителей — предупредить о дубле (только при создании)
  const debouncedTitle = useDebounced(form.title, 600)
  const similar = useSimilarTasks(
    isEdit ? '' : debouncedTitle,
    form.assignee ? [form.assignee.id, ...form.coAssignees.map((u) => u.id)] : [],
  )
  // Сколько обычно занимают похожие задачи у исполнителя — подсказка под сроком (только при создании)
  const estimate = useDurationHint(
    isEdit ? '' : debouncedTitle,
    form.assignee?.id ?? null,
    form.due.dueAt,
    form.coAssignees.map((u) => u.id),
  )
  const busy = create.isPending || patch.isPending || action.isPending
  const titleOk = form.title.trim().length > 0

  const submit = (): void => {
    setTouched(true)
    if (!titleOk) {
      titleRef.current?.focus()
      return
    }
    if (!form.assignee) {
      snackbar.show({ text: 'Выберите, кому поставить задачу' })
      setPicker('assignee')
      return
    }
    if (busy) return
    const assignee = form.assignee
    if (!isEdit && publishInvalid(form.publish)) {
      snackbar.show({ text: 'Опишите, какой пост ждём в канале' })
      return
    }

    if (!isEdit) {
      const input: CreateTaskInput = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        dod: form.dod.trim() || null,
        assigneeId: assignee.id,
        coAssigneeIds: form.coAssignees.map((u) => u.id).filter((uid) => uid !== assignee.id),
        checklist: checklistItems(form.checklistText),
        projectId: form.projectId,
        dueAt: form.due.dueAt,
        dueKind: form.due.dueKind,
        priority: form.priority,
        requiresAcceptance: self ? false : form.requiresAcceptance,
        reviewerId: !self && form.requiresAcceptance ? (form.reviewer?.id ?? null) : null,
        requiresResult: form.requiresResult,
        watcherIds: form.watchers.map((w) => w.id),
        publishCheck: publishBody(form.publish),
      }
      create.mutate(input, {
        onSuccess: (task) => {
          removeStore(DRAFT_KEY)
          if (prefillNoteId && task?.id) linkNote.mutate({ id: prefillNoteId, taskId: task.id })
          pushRecent(assignee, meId)
          setInitial(form)
          snackbar.success(
            self
              ? 'Задача создана'
              : `Задача поставлена — ${shortName(assignee.displayName)} получит уведомление`,
            task?.id ? { label: 'Открыть', onClick: () => navigate(`/tasks/${task.id}`) } : undefined,
          )
          setForm(EMPTY)
          goBack()
        },
      })
      return
    }

    if (!existing.data || !initial || editId === null) return
    const body: TaskPatchBody = { expectedVersion: existing.data.version }
    if (form.title.trim() !== initial.title) body.title = form.title.trim()
    if (form.description !== initial.description) body.description = form.description.trim() || null
    if (form.dod !== initial.dod) body.dod = form.dod.trim() || null
    if (form.priority !== initial.priority) body.priority = form.priority
    if (form.requiresAcceptance !== initial.requiresAcceptance)
      body.requiresAcceptance = form.requiresAcceptance
    if (form.requiresResult !== initial.requiresResult) body.requiresResult = form.requiresResult
    if ((form.reviewer?.id ?? null) !== (initial.reviewer?.id ?? null))
      body.reviewerId = form.reviewer?.id ?? null
    if (form.projectId !== initial.projectId) body.projectId = form.projectId
    if (form.due.dueAt !== initial.due.dueAt || form.due.dueKind !== initial.due.dueKind) {
      body.dueAt = form.due.dueAt
      body.dueKind = form.due.dueKind
    }
    const reassigned = assignee.id !== initial.assignee?.id
    const coIds = form.coAssignees.map((u) => u.id).filter((uid) => uid !== assignee.id)
    const coChanged =
      JSON.stringify(coIds) !==
      JSON.stringify(initial.coAssignees.map((u) => u.id).filter((uid) => uid !== assignee.id))
    const finish = (): void => {
      setInitial(form)
      snackbar.success('Изменения сохранены')
      goBack()
    }
    const doCo = (): void => {
      if (!coChanged) {
        finish()
        return
      }
      action.mutate(
        { taskId: editId, command: { type: 'set_co_assignees', userIds: coIds }, silent: true },
        { onSuccess: finish },
      )
    }
    const doReassign = (): void => {
      if (!reassigned) {
        doCo()
        return
      }
      action.mutate(
        { taskId: editId, command: { type: 'reassign', assigneeId: assignee.id }, silent: true },
        { onSuccess: doCo },
      )
    }
    if (Object.keys(body).length > 1) patch.mutate({ id: editId, patch: body }, { onSuccess: doReassign })
    else doReassign()
  }

  const submitRef = useRef(submit)
  useEffect(() => {
    submitRef.current = submit
  })
  // Ctrl/⌘+Enter — отправить форму
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        submitRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useMainButton({
    text: isEdit ? 'Сохранить' : self ? 'Создать' : 'Поставить задачу',
    onClick: submit,
    loading: busy,
    disabled: !titleOk || (isEdit && !dirty),
    visible: !isEdit || (initial !== null && Boolean(existing.data?.allowedActions.includes('edit'))),
  })

  if (isEdit && (existing.isPending || me.isPending)) {
    return (
      <Screen label="Изменение задачи">
        <ScreenHeader back title="Изменить задачу" small />
        <SkeletonBlock lines={4} />
      </Screen>
    )
  }
  if (isEdit && existing.isError) {
    return (
      <Screen label="Изменение задачи">
        <ScreenHeader back title="Изменить задачу" small />
        <ErrorState text={errorMessage(existing.error)} onRetry={() => void existing.refetch()} />
      </Screen>
    )
  }
  if (isEdit && existing.data && !existing.data.allowedActions.includes('edit')) {
    return (
      <Screen label="Изменение задачи">
        <ScreenHeader back title="Изменить задачу" small />
        <EmptyState
          title="Изменять задачу может только постановщик"
          text="Можно оставить комментарий в карточке задачи."
        />
      </Screen>
    )
  }

  const recent = readStore<UserBrief[]>(RECENT_KEY, [])
    .filter((u) => u.id !== meId)
    .slice(0, 5)
  const assigneeChips: UserBrief[] = [...recent]
  if (form.assignee && form.assignee.id !== meId && !assigneeChips.some((u) => u.id === form.assignee?.id)) {
    assigneeChips.unshift(form.assignee)
  }

  return (
    <Screen label={isEdit ? 'Изменение задачи' : 'Новая задача'}>
      <ScreenHeader back title={isEdit ? 'Изменить задачу' : 'Новая задача'} small />

      <div className="card">
        <Input
          ref={titleRef}
          variant="title"
          placeholder="Что нужно сделать?"
          aria-label="Название задачи"
          value={form.title}
          maxLength={200}
          enterKeyHint="next"
          onChange={(e) => set('title', e.target.value)}
        />
        {touched && !titleOk && (
          <div className="field__error">Напишите, что нужно сделать — начните с глагола</div>
        )}
      </div>
      {!isEdit && similar.data && similar.data.length > 0 && (
        <div className="hint hint--md hint--attn" role="status">
          ⚠️ Похожая задача уже есть:{' '}
          {similar.data.map((t, i) => (
            <span key={t.id}>
              {i > 0 ? ', ' : ''}
              <button type="button" className="link" onClick={() => navigate(`/tasks/${t.id}`)}>
                #{t.id} «{t.title}»
              </button>
            </span>
          ))}
          . Точно новая?
        </div>
      )}

      <Field label="Исполнитель">
        {() => (
          <div className="chips chips--wrap" role="toolbar" aria-label="Исполнитель">
            {me.data && (
              <Chip selected={self} onClick={() => me.data && set('assignee', meBrief(me.data))}>
                Мне
              </Chip>
            )}
            {assigneeChips.map((u) => (
              <Chip key={u.id} selected={form.assignee?.id === u.id} onClick={() => set('assignee', u)}>
                {shortName(u.displayName)}
              </Chip>
            ))}
            <Chip
              onClick={() => setPicker('assignee')}
              icon={<IconSearch size={18} />}
              ariaLabel="Найти сотрудника"
            >
              Найти
            </Chip>
          </div>
        )}
      </Field>
      {form.assignee && !self && (
        <div className="hint hint--md">
          Поставите: <b>{form.assignee.displayName}</b>
          {form.assignee.departmentName ? ` · ${form.assignee.departmentName}` : ''}
        </div>
      )}
      {form.assignee && !self && load.data && overloaded(load.data, form.priority) && (
        <div className="hint hint--md hint--attn" role="status">
          ⚠️ У {shortName(form.assignee.displayName)} уже {load.data.open} открытых задач
          {load.data.urgent > 0 ? `, срочных — ${load.data.urgent}` : ''}. Точно сейчас?
        </div>
      )}

      <Collapsible
        title="Ещё исполнители"
        count={form.coAssignees.length}
        defaultOpen={form.coAssignees.length > 0}
      >
        <div className="chips chips--wrap">
          {form.coAssignees.map((u) => (
            <Chip
              key={u.id}
              onRemove={() =>
                set(
                  'coAssignees',
                  form.coAssignees.filter((x) => x.id !== u.id),
                )
              }
            >
              {shortName(u.displayName)}
            </Chip>
          ))}
          <Chip onClick={() => setPicker('co')}>Добавить</Chip>
        </div>
        <div className="hint">
          Задача общая: её получат все, первый, кто возьмёт, ведёт её, а закрыть может любой.
        </div>
      </Collapsible>

      <Field label="Срок">{() => <DeadlineChips value={form.due} onChange={(v) => set('due', v)} />}</Field>
      {!isEdit && form.assignee && estimate.data?.text && (
        <div className={estimate.data.tight ? 'hint hint--md hint--attn' : 'hint hint--md'} role="status">
          ⏱ {estimate.data.text}
        </div>
      )}

      {!isEdit && (
        <PublishCheckFields value={form.publish ?? EMPTY_PUBLISH} onChange={(v) => set('publish', v)} />
      )}

      {!isEdit && (
        <div className="hint">
          <button
            type="button"
            className="link"
            onClick={() => {
              const q = new URLSearchParams()
              if (form.title.trim()) q.set('title', form.title.trim())
              if (form.assignee) q.set('assignee', String(form.assignee.id))
              const qs = q.toString()
              navigate(`/recurring/new${qs ? `?${qs}` : ''}`)
            }}
          >
            🔁 Повторять — каждый день, по будням или по выходным
          </button>
        </div>
      )}

      <Collapsible
        title="Проект или клиент"
        count={form.projectId ? 1 : 0}
        defaultOpen={form.projectId !== null}
      >
        <div className="chips chips--wrap" role="toolbar" aria-label="Проект или клиент">
          <Chip selected={form.projectId === null} onClick={() => set('projectId', null)}>
            Без проекта
          </Chip>
          {(projects.data ?? []).slice(0, 12).map((p) => (
            <Chip key={p.id} selected={form.projectId === p.id} onClick={() => set('projectId', p.id)}>
              🏷 {p.name}
            </Chip>
          ))}
        </div>
        <div className="row">
          <Input
            aria-label="Новый проект или клиент"
            placeholder="Новый проект или клиент"
            value={newProject}
            maxLength={100}
            onChange={(e) => setNewProject(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={newProject.trim().length < 2 || createProject.isPending}
            onClick={() =>
              createProject.mutate(
                { name: newProject.trim() },
                {
                  onSuccess: (p) => {
                    set('projectId', p.id)
                    setNewProject('')
                  },
                  onError: (e) => snackbar.show({ text: errorMessage(e) }),
                },
              )
            }
          >
            Добавить
          </Button>
        </div>
      </Collapsible>

      <Field
        label="Приоритет"
        hint={
          form.priority === 'urgent' ? 'Срочные задачи напоминают даже в нерабочее время (08–21).' : undefined
        }
      >
        {() => (
          <SegmentedControl
            label="Приоритет"
            options={PRIORITY_OPTIONS}
            value={form.priority}
            onChange={(v) => set('priority', v)}
          />
        )}
      </Field>

      <Field label="Готово, когда…" hint="Понятный результат экономит переписку">
        {(fid) => (
          <Textarea
            id={fid}
            rows={2}
            maxLength={300}
            placeholder="Например: КП отправлено клиенту, копия в общей папке"
            value={form.dod}
            onChange={(e) => set('dod', e.target.value)}
          />
        )}
      </Field>
      <div className="row">
        <Button
          size="sm"
          variant="secondary"
          disabled={!titleOk || suggest.isPending}
          onClick={() =>
            suggest.mutate(
              { title: form.title.trim(), description: form.description.trim() || null },
              {
                onSuccess: (r) => r.dod && set('dod', r.dod),
                onError: (e) => snackbar.show({ text: errorMessage(e) }),
              },
            )
          }
        >
          {suggest.isPending ? 'Думаю…' : '✨ Предложить «Готово, когда…»'}
        </Button>
      </div>

      <Section id="flags">
        {!self && (
          <SwitchRow
            label="Нужна проверка"
            hint={
              form.reviewer
                ? `${shortName(form.reviewer.displayName)} примет работу перед закрытием задачи`
                : 'Вы примете работу перед закрытием задачи'
            }
            checked={form.requiresAcceptance}
            onChange={(v) => set('requiresAcceptance', v)}
          />
        )}
      </Section>
      {!self && form.requiresAcceptance && (
        <Field label="Кто проверяет" hint="Проверяющий примет работу или вернёт на доработку.">
          {() => (
            <div className="chips chips--wrap">
              {form.reviewer ? (
                <Chip onRemove={() => set('reviewer', null)}>{shortName(form.reviewer.displayName)}</Chip>
              ) : (
                <Chip selected>Вы</Chip>
              )}
              <Chip onClick={() => setPicker('reviewer')}>
                {form.reviewer ? 'Другой' : 'Выбрать другого'}
              </Chip>
            </div>
          )}
        </Field>
      )}
      <Section id="flags-more">
        <SwitchRow
          label="Нужен результат"
          hint="При сдаче нужно описать итог или приложить файл"
          checked={form.requiresResult}
          onChange={(v) => set('requiresResult', v)}
        />
      </Section>

      {!isEdit && (
        <Collapsible title="Наблюдатели" count={form.watchers.length} defaultOpen={form.watchers.length > 0}>
          <div className="chips chips--wrap">
            {form.watchers.map((w) => (
              <Chip
                key={w.id}
                onRemove={() =>
                  set(
                    'watchers',
                    form.watchers.filter((x) => x.id !== w.id),
                  )
                }
              >
                {shortName(w.displayName)}
              </Chip>
            ))}
            <Chip onClick={() => setPicker('watchers')}>Добавить</Chip>
          </div>
          <div className="hint">
            Наблюдатели видят задачу и получают уведомление о выполнении.
            {self ? ' Ваши руководители добавятся сами.' : ''}
          </div>
        </Collapsible>
      )}

      {!isEdit && (
        <Collapsible
          title="Подпункты"
          count={checklistItems(form.checklistText).length}
          defaultOpen={Boolean(form.checklistText)}
        >
          <Textarea
            aria-label="Подпункты"
            rows={4}
            maxLength={4000}
            placeholder={'По одному на строку:\nСобрать цены\nСогласовать скидку\nОтправить клиенту'}
            value={form.checklistText}
            onChange={(e) => set('checklistText', e.target.value)}
          />
          <div className="hint">Исполнители отмечают пункты прямо в боте — видно, сколько уже готово.</div>
        </Collapsible>
      )}

      <Collapsible title="Описание" defaultOpen={Boolean(form.description)}>
        <Textarea
          aria-label="Описание"
          rows={4}
          maxLength={4000}
          placeholder="Контекст, ссылки, детали"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </Collapsible>

      <div className="hint">Ctrl + Enter — {isEdit ? 'сохранить' : 'поставить задачу'}</div>

      <PersonPicker
        open={picker === 'assignee'}
        onClose={() => setPicker(null)}
        title="Кому поставить"
        meId={meId}
        onPick={(u) => {
          set('assignee', u)
          setPicker(null)
        }}
      />
      <PersonPicker
        open={picker === 'co'}
        onClose={() => setPicker(null)}
        title="Ещё исполнители"
        multiple
        selectedUsers={form.coAssignees}
        excludeIds={form.assignee ? [form.assignee.id] : []}
        onDone={(list) => {
          set('coAssignees', list)
          setPicker(null)
        }}
      />
      <PersonPicker
        open={picker === 'reviewer'}
        onClose={() => setPicker(null)}
        title="Кто проверяет"
        meId={meId}
        excludeIds={[...(form.assignee ? [form.assignee.id] : []), ...form.coAssignees.map((u) => u.id)]}
        onPick={(u) => {
          set('reviewer', u.id === meId ? null : u)
          setPicker(null)
        }}
      />
      <PersonPicker
        open={picker === 'watchers'}
        onClose={() => setPicker(null)}
        title="Наблюдатели"
        multiple
        selectedUsers={form.watchers}
        excludeIds={[meId, ...(form.assignee ? [form.assignee.id] : [])]}
        onDone={(list) => {
          set('watchers', list)
          setPicker(null)
        }}
      />
    </Screen>
  )
}
